import { Flex } from '@chakra-ui/react';
import { FormFieldSelect } from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import SelectUpdateModal from './SelectUpdateModal';
import { FieldHeader } from '../common/FieldHeader';
import { SelectDisplay } from './SelectDisplay';
import { SelectDiffModal } from './SelectDiffModal';
import { SelectEmptyState } from './SelectEmptyState';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

type IProps = {
  formFieldDefinition: FormFieldSelect;
  value: string;
  previousValue?: string | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: string) => void;
  entryPoint: EntryPoint;
  submissionContextId: string;
  isUpdateRequired?: boolean;
};

export const GenericSelectField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, getExternalDependency } =
    useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const externalDependency = getExternalDependency(
    formFieldDefinition.params.externalFieldDependency?.path
  );

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && formFieldDefinition.name !== 'FocusIssue' && (
        <SelectUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: string) => {
            setIsEditing(false);
            onChange(value);
          }}
          externalDependency={externalDependency}
        />
      )}

      {isUpdated && showDiffModal ? (
        <SelectDiffModal
          fieldName={formFieldDefinition.params.labels.main}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <SelectDisplay
          value={value}
          subtitle={
            formFieldDefinition.params.labels.options[value]?.secondary!
          }
        />
      ) : (
        <SelectEmptyState />
      )}
    </Flex>
  );
};
